import {
  getListClassesNames,
  getListClients,
  getListStates,
} from 'helpers/genericHelper';
import { getUserProfile } from 'helpers/userProfile';
import { ReactElement, useEffect, useState } from 'react';
import { useFilterContext } from '../../contexts/FilterContext';
import { FilterOptions, GroupOption } from '../../types';
import { Option } from '../SelectField';
import { FilterFields } from './FilterFields';
import { FilterFieldsContainer } from './styles';
import { useMediaQuery } from '~/hooks/useMediaQuery';
import { FilterButton } from './FilterButton';

export function Filter(): ReactElement {
  const [profile] = useState(getUserProfile);
  const [loading, setLoading] = useState(false);
  const {
    onSelectClient, filterOptions, setFilterOptions,
  } = useFilterContext();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [isCollapsed, setIsCollapsed] = useState(true);
  const hasClientInput = !!(profile.manageAllClients || profile.manageMultipleClients);

  async function getOptions(): Promise<void> {
    setLoading(true);
    const statesList = await getListStates();
    const newFilterOptions: FilterOptions = {
      states: [],
      groups: [],
      clients: [],
    };

    const profileManageMoreThanOneClient = profile.manageAllClients || profile.permissions.PER_CLIENT.length > 1;

    if (profileManageMoreThanOneClient) {
      const clientsList = await getListClients();
      let allowedClients = clientsList;
      if (clientsList.length > 0) {
        if (!profile.manageAllClients) {
          const allowedIds = new Set(profile.permissions.PER_CLIENT.map((c) => c.clientId));

          allowedClients = clientsList.filter((c) => allowedIds.has(Number(c.value)));
        }

        newFilterOptions.clients.push(
          ...allowedClients.map(
            (item): Option => ({
              label: item.name,
              value: item.value,
              iconName: 'User1Icon',
            }),
          ),
        );
      }
    } else {
      const groupList = await getListClassesNames();
      if (groupList.length > 0) {
        newFilterOptions.groups.push(
          ...groupList.map(
            (item): GroupOption => ({
              label: item.name,
              value: item.value,
              iconName: 'LayoutGrid1Icon',
              units: item.units,
            }),
          ),
        );
      }
    }

    if (statesList.length > 0) {
      newFilterOptions.states.push(
        ...statesList.map(
          (item): Option => ({
            label: item.name,
            value: item.value,
            iconName: 'MarkerPin1Icon',
          }),
        ),
      );
    }

    setFilterOptions(newFilterOptions);
    handleClientByPermission();
    setLoading(false);
  }

  function handleClientByPermission(): void {
    const santanderIds = [21, 59, 120, 152];
    const isSantanderClient = profile.permissions.PER_CLIENT.some((p) => santanderIds.includes(p.clientId));

    if ((profile.manageAllClients || profile.manageMultipleClients) && !isSantanderClient) return;
    let clientId: number | null = null;

    if (isSantanderClient) {
      clientId = profile.permissions.PER_CLIENT[profile.permissions.PER_CLIENT.length - 1].clientId;
    } else {
      clientId = profile.permissions.PER_CLIENT[0].clientId;
    }

    if (clientId) {
      onSelectClient(clientId.toString());
    }
  }

  function onClickFilterButton(): void {
    setIsCollapsed((previousValue) => !previousValue);
  }

  useEffect(() => {
    getOptions();
  }, []);

  return (
    <FilterFieldsContainer hasClientInput={hasClientInput}>
      {isMobile && (
        <FilterButton
          collapsed={isCollapsed}
          onClick={onClickFilterButton}
          disabled={false}
        />
      )}
      {(!isMobile || !isCollapsed) && (
        <FilterFields
          filterOptions={filterOptions}
          disabled={loading}
        />
      )}
    </FilterFieldsContainer>
  );
}
